// Számla-kiállítási életciklus (Számlázz.hu) kliens-oldali logika — a lista ÉS a detail közös forrása.
// Mutációk: kiállítás / sztornó / díjbekérő-érvénytelenítés / státusz-szinkron; + PDF-letöltés (base64 →
// Blob, SOHA nem tárolódik). A ts-rest kliens 4xx/5xx-re NEM dob (a promise `{status,body}`-vel resolve-ol)
// → a státusz-alapú toast-terelést itt végezzük. A siker → cache-invalidálás + siker-toast.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type TFunction } from 'i18next';
import { useState } from 'react';
import { toast } from 'sonner';

import { invoiceKeys } from './api';

import { api } from '@/lib/api';

/** Egy státusz-művelet változói (a fej OCC-verziójával). */
export interface InvoiceActionVars {
  id: string;
  version: number;
}
/**
 * A KIÁLLÍTÁS változói: a három dátum, amit a megerősítő ablak előre kitöltve mutat (a kiállítás napjára
 * igazítva, `shiftIssueDates`). Ami innen kimegy, az kerül a bizonylatra — ha a mező üres, a szerver számol.
 */
export interface InvoiceIssueVars extends InvoiceActionVars {
  issueDate?: string | undefined;
  fulfillmentDate?: string | undefined;
  dueDate?: string | undefined;
}

/** A sztornó változói (+ opcionális megjegyzés). */
export interface InvoiceStornoVars extends InvoiceActionVars {
  stornoComment: string | null;
}

/** Nem-200 életciklus-státusz → hiba-toast (503/502 saját üzenettel; a többi a közös terelés). */
function actionErrorToast(status: number, t: TFunction): void {
  const key =
    status === 503
      ? 'toast:invoices.notConfigured'
      : status === 502
        ? 'toast:invoices.szamlazzError'
        : status === 409 || status === 428
          ? 'toast:invoices.conflict'
          : status === 403
            ? 'toast:invoices.forbidden'
            : status === 422
              ? 'toast:invoices.validation'
              : 'toast:invoices.error';
  toast.error(t(key));
}

/** Base64 PDF → letöltés (Blob + object-URL; SOHA nem tárolódik szerver-oldalon). */
function triggerPdfDownload(contentBase64: string, filename: string): void {
  const binary = atob(contentBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/**
 * Az életciklus-mutációk + PDF-letöltés közös hookja. A `onDone` a sikeres státusz-művelet után fut (pl.
 * a megnyitott dialógus zárása). A cache-invalidálás a lista + az érintett detail kulcsra.
 */
export function useInvoiceLifecycle(t: TFunction, onDone?: () => void) {
  const qc = useQueryClient();
  const [pdfPendingId, setPdfPendingId] = useState<string | null>(null);

  const invalidate = (id: string): void => {
    void qc.invalidateQueries({ queryKey: invoiceKeys.lists() });
    void qc.invalidateQueries({ queryKey: invoiceKeys.detail(id) });
  };

  const issue = useMutation({
    mutationFn: ({ id, version, issueDate, fulfillmentDate, dueDate }: InvoiceIssueVars) =>
      api.invoiceIssue.mutation({
        params: { id },
        headers: { 'if-match': String(version) },
        // ÜRES mező = „számolj te": a szerver ilyenkor a kiállítás napjára igazít. A tömeges kiállítás
        // sosem küld dátumot, tehát a szerver-oldali számítás az egyetlen forrás-igazság.
        body: { issueDate, fulfillmentDate, dueDate },
      }),
    onSuccess: (res, vars) => {
      if (res.status === 200) {
        toast.success(t('toast:invoices.issued'));
        invalidate(vars.id);
        onDone?.();
      } else actionErrorToast(res.status, t);
    },
    onError: () => toast.error(t('toast:invoices.error')),
  });

  const storno = useMutation({
    mutationFn: ({ id, version, stornoComment }: InvoiceStornoVars) =>
      api.invoiceStorno.mutation({
        params: { id },
        headers: { 'if-match': String(version) },
        body: { stornoComment },
      }),
    onSuccess: (res, vars) => {
      if (res.status === 200) {
        toast.success(t('toast:invoices.stornoed'));
        invalidate(vars.id);
        onDone?.();
      } else actionErrorToast(res.status, t);
    },
    onError: () => toast.error(t('toast:invoices.error')),
  });

  const cancelProforma = useMutation({
    mutationFn: ({ id, version }: InvoiceActionVars) =>
      api.invoiceCancelProforma.mutation({
        params: { id },
        headers: { 'if-match': String(version) },
      }),
    onSuccess: (res, vars) => {
      if (res.status === 200) {
        toast.success(t('toast:invoices.cancelled'));
        invalidate(vars.id);
        onDone?.();
      } else actionErrorToast(res.status, t);
    },
    onError: () => toast.error(t('toast:invoices.error')),
  });

  /**
   * TÖMEGES kiállítás (a legacy „bulk issue"). A hívó CSAK id-kat ad — a fej-verziót soronként a SZERVER olvassa
   * (a listának nincs friss verziója minden kijelölt sorra). A toast a legacy bontását követi: típusonként
   * (számla/díjbekérő) siker- és hiba-darabszám; RÉSZLEGES hibánál warning, teljes hibánál error.
   */
  const bulkIssue = useMutation({
    mutationFn: ({ ids }: { ids: string[] }) => api.invoiceBulkIssue.mutation({ body: { ids } }),
    onSuccess: (res) => {
      if (res.status !== 200) {
        actionErrorToast(res.status, t);
        return;
      }
      const s = res.body.summary;
      const ok = s.successInvoice + s.successProforma;
      const failed = s.failedInvoice + s.failedProforma;
      const msg = t('toast:invoices.bulkIssued', {
        invoice: s.successInvoice,
        proforma: s.successProforma,
      });
      if (failed === 0) toast.success(msg);
      else if (ok === 0)
        toast.error(
          t('toast:invoices.bulkIssueFailed', {
            invoice: s.failedInvoice,
            proforma: s.failedProforma,
          }),
        );
      else
        toast.warning(
          `${msg} — ${t('toast:invoices.bulkIssuePartial', {
            invoice: s.failedInvoice,
            proforma: s.failedProforma,
          })}`,
        );
      void qc.invalidateQueries({ queryKey: invoiceKeys.lists() });
      onDone?.();
    },
    onError: () => toast.error(t('toast:invoices.error')),
  });

  const sync = useMutation({
    mutationFn: () => api.invoiceSyncStatus.mutation({}),
    onSuccess: (res) => {
      if (res.status === 200) {
        toast.success(
          t('toast:invoices.synced', {
            paid: res.body.paid,
            overdue: res.body.overdue,
            related: res.body.related,
          }),
        );
        void qc.invalidateQueries({ queryKey: invoiceKeys.lists() });
      } else if (res.status === 409) {
        toast.error(t('toast:invoices.syncLocked'));
      } else actionErrorToast(res.status, t);
    },
    onError: () => toast.error(t('toast:invoices.error')),
  });

  /** PDF-letöltés (az alap vagy a sztornó számlaszámra). Hiba → státusz-alapú toast. */
  async function downloadPdf(id: string, which: 'invoice' | 'storno'): Promise<void> {
    setPdfPendingId(id);
    try {
      const res = await api.invoicePdf.query({ params: { id }, query: { which } });
      if (res.status === 200) {
        triggerPdfDownload(res.body.contentBase64, res.body.filename);
      } else {
        actionErrorToast(res.status, t);
      }
    } catch {
      toast.error(t('toast:invoices.error'));
    } finally {
      setPdfPendingId(null);
    }
  }

  return { issue, bulkIssue, storno, cancelProforma, sync, downloadPdf, pdfPendingId };
}
